// =============================================================================
// hooks/sales/useCampaignDetail.ts — Camada de dados do painel de UMA
// campanha (`GET/PATCH /api/sales/campaigns/:id`, ações de ciclo de vida e
// carteira paginada — F35-S09, doc 26 §6.7).
//
// Mesmo padrão de hooks/sales/useCampaigns.ts/useAppointments.ts: contrato
// vem 100% de `@elemento/shared-schemas` (nenhum campo redeclarado), parse
// runtime defensivo em toda resposta, sem cálculo de negócio no cliente.
//
// Query key `['sales','campaigns','detail', id]` é a MESMA usada por
// features/sales/campaigns/operate/useCampaignSummary.ts (F35-S10, arquivo
// fora de `files_allowed` deste slot — não editado, só compartilha o
// namespace de cache por convenção: mesmo endpoint, mesmo shape, evita
// refetch duplicado ao navegar detalhe → operar).
//
// LIMITAÇÃO CONHECIDA (mesma norma de useCampaigns.ts): nenhum agregado de
// KPI/progresso é publicado pela API para uma campanha (contagem de membros
// por status É publicada — `GET /members?status=X&limit=1`, `total` real do
// servidor, mesmo padrão de `useCampaignsCount`). O componente de
// visualização decide como mostrar "indisponível" quando aplicável — este
// hook nunca fabrica número.
// =============================================================================

import type {
  Campaign,
  CampaignMember,
  CampaignMembersListQuery,
  CampaignMembersListResponse,
  CampaignPatchBody,
} from '@elemento/shared-schemas';
import {
  CampaignMembersListResponseSchema,
  CampaignMemberSchema,
  CampaignSchema,
} from '@elemento/shared-schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '../../lib/api';

export type { Campaign, CampaignMember, CampaignMembersListQuery, CampaignMembersListResponse };

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const campaignDetailKeys = {
  detail: (campaignId: string) => ['sales', 'campaigns', 'detail', campaignId] as const,
  members: (campaignId: string, filters: CampaignMembersFilters) =>
    ['sales', 'campaigns', 'detail', campaignId, 'members', filters] as const,
};

// ---------------------------------------------------------------------------
// GET /api/sales/campaigns/:id
// ---------------------------------------------------------------------------

async function fetchCampaign(campaignId: string): Promise<Campaign> {
  const raw = await api.get<unknown>(`/api/sales/campaigns/${encodeURIComponent(campaignId)}`);
  return CampaignSchema.parse(raw);
}

export interface UseCampaignDetailResult {
  campaign: Campaign | undefined;
  isLoading: boolean;
  isError: boolean;
  /** `true` quando o servidor respondeu 404 — campanha inexistente ou fora do escopo do ator. */
  isNotFound: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useCampaignDetail(
  campaignId: string | undefined,
  opts: { enabled?: boolean } = {},
): UseCampaignDetailResult {
  const enabled = (opts.enabled ?? true) && Boolean(campaignId);
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: campaignDetailKeys.detail(campaignId ?? ''),
    queryFn: () => fetchCampaign(campaignId as string),
    enabled,
    // 404 é um resultado estável (campanha não existe/fora de escopo) —
    // retentar não muda o desfecho e só atrasa o estado "não encontrada".
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) return false;
      return failureCount < 1;
    },
  });

  const isNotFound = isError && error instanceof ApiError && error.status === 404;

  return {
    campaign: data,
    isLoading: enabled && isLoading,
    isError,
    isNotFound,
    error: error as Error | null,
    refetch: () => void refetch(),
  };
}

// ---------------------------------------------------------------------------
// PATCH /api/sales/campaigns/:id
// ---------------------------------------------------------------------------

async function patchCampaign(campaignId: string, body: CampaignPatchBody): Promise<Campaign> {
  const raw = await api.patch<unknown>(
    `/api/sales/campaigns/${encodeURIComponent(campaignId)}`,
    body,
  );
  return CampaignSchema.parse(raw);
}

export function useUpdateCampaign(campaignId: string): {
  mutateAsync: (body: CampaignPatchBody) => Promise<Campaign>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (body: CampaignPatchBody) => patchCampaign(campaignId, body),
    onSuccess: (updated) => {
      queryClient.setQueryData(campaignDetailKeys.detail(campaignId), updated);
    },
  });

  return { mutateAsync: mutation.mutateAsync, isPending: mutation.isPending };
}

// ---------------------------------------------------------------------------
// POST /api/sales/campaigns/:id/activate|pause|resume — idempotentes no
// servidor (já no estado alvo → 200 sem efeito colateral, ver service.ts).
// O botão do cabeçalho ainda se desabilita durante `isPending` como reforço
// de UI contra duplo clique (DoD do slot), mas a garantia real é do backend.
// ---------------------------------------------------------------------------

async function postCampaignTransition(
  campaignId: string,
  action: 'activate' | 'pause' | 'resume',
): Promise<Campaign> {
  const raw = await api.post<unknown>(
    `/api/sales/campaigns/${encodeURIComponent(campaignId)}/${action}`,
    {},
  );
  return CampaignSchema.parse(raw);
}

function useCampaignTransition(
  campaignId: string,
  action: 'activate' | 'pause' | 'resume',
): { mutateAsync: () => Promise<Campaign>; isPending: boolean } {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => postCampaignTransition(campaignId, action),
    onSuccess: (updated) => {
      queryClient.setQueryData(campaignDetailKeys.detail(campaignId), updated);
    },
  });

  return { mutateAsync: () => mutation.mutateAsync(), isPending: mutation.isPending };
}

export function usePauseCampaign(campaignId: string): {
  mutateAsync: () => Promise<Campaign>;
  isPending: boolean;
} {
  return useCampaignTransition(campaignId, 'pause');
}

export function useResumeCampaign(campaignId: string): {
  mutateAsync: () => Promise<Campaign>;
  isPending: boolean;
} {
  return useCampaignTransition(campaignId, 'resume');
}

// ---------------------------------------------------------------------------
// GET /api/sales/campaigns/:id/members — carteira paginada (F35-S04).
// ---------------------------------------------------------------------------

export interface CampaignMembersFilters {
  status?: CampaignMembersListQuery['status'];
  assignedAgentId?: CampaignMembersListQuery['assignedAgentId'];
  nextActionAfter?: string;
  nextActionBefore?: string;
  sortBy?: CampaignMembersListQuery['sortBy'];
  sortOrder?: CampaignMembersListQuery['sortOrder'];
  limit?: number;
  offset?: number;
}

function buildMembersQuery(filters: CampaignMembersFilters): string {
  const qs = new URLSearchParams();

  for (const s of filters.status ?? []) qs.append('status', s);
  for (const id of filters.assignedAgentId ?? []) qs.append('assignedAgentId', id);

  if (filters.nextActionAfter !== undefined) qs.set('nextActionAfter', filters.nextActionAfter);
  if (filters.nextActionBefore !== undefined) qs.set('nextActionBefore', filters.nextActionBefore);
  if (filters.sortBy !== undefined) qs.set('sortBy', filters.sortBy);
  if (filters.sortOrder !== undefined) qs.set('sortOrder', filters.sortOrder);
  if (filters.limit !== undefined) qs.set('limit', String(filters.limit));
  if (filters.offset !== undefined) qs.set('offset', String(filters.offset));

  return qs.toString();
}

async function fetchCampaignMembers(
  campaignId: string,
  filters: CampaignMembersFilters,
): Promise<CampaignMembersListResponse> {
  const query = buildMembersQuery(filters);
  const raw = await api.get<unknown>(
    `/api/sales/campaigns/${encodeURIComponent(campaignId)}/members${query ? `?${query}` : ''}`,
  );
  return CampaignMembersListResponseSchema.parse(raw);
}

export function useCampaignMembers(
  campaignId: string,
  filters: CampaignMembersFilters,
  opts: { enabled?: boolean } = {},
): {
  data: CampaignMembersListResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const enabled = opts.enabled ?? true;
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: campaignDetailKeys.members(campaignId, filters),
    queryFn: () => fetchCampaignMembers(campaignId, filters),
    enabled,
    placeholderData: (prev) => prev,
  });

  return { data, isLoading, isError, error: error as Error | null, refetch: () => void refetch() };
}

/** Total de membros de um recorte, sem carregar a lista (limit=1 — mesmo padrão de useCampaignsCount). */
export function useCampaignMembersCount(
  campaignId: string,
  filters: Omit<CampaignMembersFilters, 'limit' | 'offset'>,
  opts: { enabled?: boolean } = {},
): { total: number | undefined; isLoading: boolean; isError: boolean } {
  const countFilters: CampaignMembersFilters = { ...filters, limit: 1, offset: 0 };
  const enabled = opts.enabled ?? true;
  const { data, isLoading, isError } = useQuery({
    queryKey: campaignDetailKeys.members(campaignId, countFilters),
    queryFn: () => fetchCampaignMembers(campaignId, countFilters),
    enabled,
    placeholderData: (prev) => prev,
  });

  return { total: data?.total, isLoading, isError };
}

// ---------------------------------------------------------------------------
// PATCH /api/sales/campaigns/:id/members/:memberId — remove com motivo
// (F35-S04). Invalida toda a árvore de membros desta campanha — o item some
// da página atual sem depender de patch otimista sobre uma lista paginada.
// ---------------------------------------------------------------------------

async function removeCampaignMember(
  campaignId: string,
  memberId: string,
  reason: string,
): Promise<CampaignMember> {
  const raw = await api.patch<unknown>(
    `/api/sales/campaigns/${encodeURIComponent(campaignId)}/members/${encodeURIComponent(memberId)}`,
    { reason },
  );
  const parsed = raw as { member: unknown };
  return CampaignMemberSchema.parse(parsed.member);
}

export function useRemoveCampaignMember(campaignId: string): {
  mutateAsync: (args: { memberId: string; reason: string }) => Promise<CampaignMember>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ memberId, reason }: { memberId: string; reason: string }) =>
      removeCampaignMember(campaignId, memberId, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['sales', 'campaigns', 'detail', campaignId, 'members'],
      });
    },
  });

  return { mutateAsync: mutation.mutateAsync, isPending: mutation.isPending };
}
